import { useMemo } from 'react';
import { getAiPromptType } from '@/util';
import type { TraditionalLTCQuoteWithROI, QuoteWithROI } from '@/features/shop-insurance';

interface UseUnderwritingResultsProps {
  adjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
  searchResults: Map<
    string,
    { status: 'eligible' | 'ineligible' | 'unsure' | null; fullData?: any }
  >;
  selectedTab: ShopInsuranceTab;
  underwritingFetchComplete: boolean;
  selectedEligibilityFilters: string[];
}

export function useUnderwritingResults({
  adjustedQuotes,
  filteredTraditionalLTCQuotes,
  searchResults,
  selectedTab,
  underwritingFetchComplete,
  selectedEligibilityFilters,
}: UseUnderwritingResultsProps) {
  const underwritingResults = useMemo(() => {
    const results = new Map<string, 'eligible' | 'ineligible' | 'unsure' | null>();

    if (searchResults.size === 0) {
      return results;
    }

    adjustedQuotes.forEach(quote => {
      const aiPromptType = getAiPromptType('', quote.productName, selectedTab === 'joint');
      if (aiPromptType) {
        const resultKey = `${quote.productName}:${aiPromptType}`;
        const underwritingResult = searchResults.get(resultKey);
        if (underwritingResult !== undefined) {
          results.set(quote.insuranceQuoteId, underwritingResult.status);
        }
      }
    });

    filteredTraditionalLTCQuotes.forEach(quote => {
      const aiPromptType = getAiPromptType('', quote.productName, selectedTab === 'joint');
      if (aiPromptType) {
        const resultKey = `${quote.productName}:${aiPromptType}`;
        const underwritingResult = searchResults.get(resultKey);
        if (underwritingResult !== undefined) {
          results.set(quote.traditionalLTCQuoteId, underwritingResult.status);
        }
      }
    });

    return results;
  }, [searchResults, adjustedQuotes, filteredTraditionalLTCQuotes, selectedTab]);

  const eligibilityCounts = useMemo(
    () => ({
      eligible:
        adjustedQuotes.filter(
          quote => underwritingResults.get(quote.insuranceQuoteId) === 'eligible',
        ).length +
        filteredTraditionalLTCQuotes.filter(
          quote => underwritingResults.get(quote.traditionalLTCQuoteId) === 'eligible',
        ).length,
      unsure:
        adjustedQuotes.filter(quote => underwritingResults.get(quote.insuranceQuoteId) === 'unsure')
          .length +
        filteredTraditionalLTCQuotes.filter(
          quote => underwritingResults.get(quote.traditionalLTCQuoteId) === 'unsure',
        ).length,
      ineligible:
        adjustedQuotes.filter(
          quote => underwritingResults.get(quote.insuranceQuoteId) === 'ineligible',
        ).length +
        filteredTraditionalLTCQuotes.filter(
          quote => underwritingResults.get(quote.traditionalLTCQuoteId) === 'ineligible',
        ).length,
    }),
    [adjustedQuotes, filteredTraditionalLTCQuotes, underwritingResults],
  );

  const filteredAdjustedQuotes = useMemo(() => {
    if (!underwritingFetchComplete || selectedEligibilityFilters.length === 0) {
      return adjustedQuotes;
    }

    return adjustedQuotes.filter(quote => {
      const status = underwritingResults.get(quote.insuranceQuoteId);
      if (!status) return true;
      return selectedEligibilityFilters.includes(status);
    });
  }, [adjustedQuotes, underwritingResults, underwritingFetchComplete, selectedEligibilityFilters]);

  const filteredTraditionalLTCQuotesWithEligibility = useMemo(() => {
    if (!underwritingFetchComplete || selectedEligibilityFilters.length === 0) {
      return filteredTraditionalLTCQuotes;
    }

    return filteredTraditionalLTCQuotes.filter(quote => {
      const status = underwritingResults.get(quote.traditionalLTCQuoteId);
      if (!status) return true;
      return selectedEligibilityFilters.includes(status);
    });
  }, [
    filteredTraditionalLTCQuotes,
    underwritingResults,
    underwritingFetchComplete,
    selectedEligibilityFilters,
  ]);

  const hasValidUnderwritingResults = Array.from(underwritingResults.values()).some(
    result => result !== null,
  );

  const showEligibilityFilter = underwritingFetchComplete && hasValidUnderwritingResults;

  return {
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  };
}
